import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import Dialog from '../Dialog';
import Toolbar from '../../page/BedStatus/newComponents/Toolbar';
import { BS_TOOLBAR_STATUS } from '../../comm/constant';
import { bedStatusAPI } from '../../apis';
import FloorPlan from '../../page/BedStatus/newComponents/Landing/FloorPlan';
import QueueBar from '../../page/BedStatus/newComponents/Landing/QueueBar';
import { updateBedStatusViewVal } from '../../redux/actions';
import { CUBICLE_VIEW2 } from '../../redux/constant';

const useStyles = makeStyles(() => ({
  root: ({ toolStatus }) => {
    let style = {
      scale: toolStatus.zoom,
      transformOrigin: 'left top',
      cursor: 'auto',
      userSelect: 'auto',
    };

    if (toolStatus.type === BS_TOOLBAR_STATUS.MOUSE) {
      style.cursor = 'auto';
      style.userSelect = 'auto';
    } else if (toolStatus.type === BS_TOOLBAR_STATUS.MOVE) {
      style.cursor = 'grab';
      style.userSelect = 'none';
    }

    return style;
  },
}));

const CubicleView = ({ wardLocation, onClose, hospCode, wardId }) => {
  const WardBoxRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const [popup, setPopup] = useState(false);
  const [floorPlan, setFloorPlan] = useState([]);
  const { defaultViewType } = useSelector((state) => state.bedStatusView);
  const [toolStatus, setToolStatus] = useState({ type: BS_TOOLBAR_STATUS.MOVE, zoom: 1, reset: 0 });
  const classes = useStyles({ toolStatus, isDragging });
  const dispatch = useDispatch();
  const queueBarRef = useRef(null);
  const [bedStatusQueueList, setBedStatusQueueList] = useState(null);
  const [showQueueBar, setShowQueueBar] = useState(false);

  const getFloorPlanLocation = useCallback(async () => {
    try {
      const params = {
        hospCode,
        wardId,
        wardLocation,
      };
      const { result } = await bedStatusAPI.getFloorPlanLocation(params);
      setFloorPlan(result?.floorplans);
      setBedStatusQueueList(result?.queuebar);
    } catch (error) {}
  }, [hospCode, wardLocation, wardId]);

  useEffect(() => {
    getFloorPlanLocation();
  }, []);

  useEffect(() => {
    WardBoxRef?.current?.scroll(0, 0);
  }, [toolStatus.reset]);

  const handleMouseDown = useCallback(
    (event) => {
      if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE || popup) {
        return;
      }
      setIsDragging(true);
      setStartPosition({
        x: event.clientX,
        y: event.clientY,
        scrollLeft: WardBoxRef.current.scrollLeft,
        scrollTop: WardBoxRef.current.scrollTop,
      });
    },
    [toolStatus, WardBoxRef, popup]
  );

  const handleMouseMove = useCallback(
    (event) => {
      if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE || popup) {
        return;
      }
      if (isDragging) {
        const deltaX = event.clientX - startPosition.x;
        const deltaY = event.clientY - startPosition.y;
        const scrollLeft = startPosition.scrollLeft - deltaX;
        const scrollTop = startPosition.scrollTop - deltaY;
        WardBoxRef.current.scroll(scrollLeft, scrollTop);
      }
    },
    [toolStatus, startPosition, isDragging, popup]
  );

  const handleTouchStart = useCallback(
    (event) => {
      if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE || popup) {
        return;
      }
      setIsDragging(true);
      setStartPosition({
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY,
        scrollLeft: WardBoxRef.current.scrollLeft,
        scrollTop: WardBoxRef.current.scrollTop,
      });
    },
    [toolStatus, WardBoxRef, popup]
  );

  const handleTouchMove = useCallback(
    (event) => {
      if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE || popup) {
        return;
      }
      if (isDragging) {
        const deltaX = event.changedTouches[0].clientX - startPosition.x;
        const deltaY = event.changedTouches[0].clientY - startPosition.y;
        const scrollLeft = startPosition.scrollLeft - deltaX;
        const scrollTop = startPosition.scrollTop - deltaY;
        WardBoxRef.current.scroll(scrollLeft, scrollTop);
      }
    },
    [toolStatus, startPosition, isDragging, popup]
  );

  const handleMouseUp = useCallback(() => {
    if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE) {
      return;
    }
    setIsDragging(false);
  }, [toolStatus]);

  const handleMouseLeave = useCallback(() => {
    if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE) {
      return;
    }
    setIsDragging(false);
  }, [toolStatus]);

  const handleClose = () => {
    dispatch(
      updateBedStatusViewVal({ currentViewType: defaultViewType, isCubicleV2: defaultViewType === CUBICLE_VIEW2 })
    );
    onClose();
  };

  return (
    <Dialog
      open={true}
      haveFoot={false}
      width="100vw"
      dialogRootStyle={{ height: '100vh', overflow: 'hidden' }}
      handleClose={handleClose}
    >
      <Box display="flex" height="100%" gridGap="0.857rem">
        <Box display="flex">
          <QueueBar
            ref={queueBarRef}
            showPatientName={false}
            bedStatusQueueList={bedStatusQueueList}
            getFloorPlanListAPI={() => {}}
            BsMlookupList={[]}
            showRemarkBar={false}
            showQueueBar={showQueueBar}
            setShowQueueBar={setShowQueueBar}
            filter={{ wardLocation: 'All' }}
            readOnly={true}
          />
          <Toolbar setStatus={setToolStatus} status={toolStatus} />
        </Box>
        <Box
          display="flex"
          ref={WardBoxRef}
          overflow="hidden"
          maxHeight="80vh"
          minHeight="80vh"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleMouseUp}
          onTouchCancel={handleMouseLeave}
        >
          <Box className={classes.root} display="flex" flexDirection="column" gridGap="2rem">
            {floorPlan.map((item) => (
              <FloorPlan
                key={item.hospCode + item.wardLocation}
                allData={item}
                getFloorPlanListAPI={getFloorPlanLocation}
                setPopup={setPopup}
                filterData={null}
                handleRouteToTransferIn={() => {}}
                readOnly={true}
                admission={true}
                queueBarRef={queueBarRef}
                showQueueBar={showQueueBar}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CubicleView;
